import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  getMenus,
  getRaccourcisByid,
  updateRaccourci,
} from "../Services/MenuApi";
import swal from "sweetalert";
import GifLoader from "../Components/loader";
import DraggableProduct from "../Components/draggable";
import PlaceholderSlot from "../Components/dropSlot";
import { useOrganisations } from "../Auth/useOrganisations";

function EditRaccourci() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [raccourci, setRaccourci] = useState(null);
  const [menus, setMenus] = useState([]);
  const [shortcuts, setShortcuts] = useState(Array(12).fill(null));
  const [raccourciName, setRaccourciName] = useState("");
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("");
  const [
    organisations,
    setOrganisations,
    usedOrganisations,
    setUsedOrganisations,
  ] = useOrganisations();
  const loadData = async () => {
    try {
      setLoading(true);

      // Load raccourci
      const raccourciResponse = await getRaccourcisByid(id);

      // Check if response is successful
      if (raccourciResponse.success) {
        const raccourciData = raccourciResponse.data.data;
        setRaccourci(raccourciData);
        setRaccourciName(raccourciData.raccourci_name);

        // Initialize shortcuts array with 12 empty slots
        const newShortcuts = Array(12).fill(null);

        // Map items to their positions using the product data from the API response
        if (raccourciData.items && raccourciData.items.length > 0) {
          raccourciData.items.forEach((item) => {
            const position = item.position;

            // Validate position is within 0-11 range
            if (position >= 0 && position < 12) {
              if (item.product_id) {
                // Use the complete product data from the API response
                // Format it to match what DraggableProduct expects
                newShortcuts[position] = {
                  _id: item.product_id._id,
                  titre: item.product_id.titre,
                  image: item.product_id.image,
                  prix: item.product_id.prix_ttc,
                  description: item.product_id.description,
                  // Add any other fields that DraggableProduct might need
                  organisation_id: item.product_id.organisation_id,
                  category_id: item.product_id.category_id,
                  // You can also keep the original item data if needed
                  originalItem: item,
                };
              }
            } else {
              console.warn(`Invalid position ${position} for item:`, item);
            }
          });
        }

        setShortcuts(newShortcuts);
        console.log("Loaded shortcuts from raccourci:", newShortcuts);

        // We still need menus for the left side (available products to drag)
        // But we can load this separately or keep it for drag source
        const menusResponse = await getMenus(usedOrganisations);
        if (menusResponse.data.success !== "0") {
          const allMenus = menusResponse.data.data;
          setMenus(allMenus);

          // Extract currency from any product
          if (allMenus.length > 0) {
            setCurrency("DT"); // Based on your data showing DT
          }
        }
      } else {
        throw new Error("Failed to load raccourci data");
      }
    } catch (error) {
      console.error("Error loading data:", error);
      swal("Erreur!", "Impossible de charger le raccourci", "error");
      navigate("/raccourcis");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, [id, usedOrganisations]);

  const handleDrop = (product, slotIndex) => {
    const newShortcuts = [...shortcuts];
    newShortcuts[slotIndex] = product;
    setShortcuts(newShortcuts);
  };

  const removeShortcut = (slotIndex) => {
    const newShortcuts = [...shortcuts];
    newShortcuts[slotIndex] = null;
    setShortcuts(newShortcuts);
  };

  const clearShortcuts = () => {
    swal({
      title: "Effacer tous les raccourcis?",
      text: "Tous les produits seront retirés de la grille.",
      icon: "warning",
      buttons: ["Annuler", "Effacer"],
      dangerMode: true,
    }).then((willClear) => {
      if (willClear) {
        setShortcuts(Array(12).fill(null));
      }
    });
  };

  const saveChanges = async () => {
    try {
      // Filter only filled slots
      console.log("shortcuts", shortcuts);
      const validShortcuts = shortcuts
        .map((shortcut, index) => {
          if (shortcut) {
            return {
              position: index,
              // Existing slots carry `originalItem` (from loadData); a product
              // freshly dragged in from "Produits disponibles" doesn't, so it
              // falls back to its own id.
              product_id:
                shortcut.originalItem?.product_id?._id ||
                shortcut.originalItem?.product_id?.id ||
                shortcut._id ||
                shortcut.id,
              product_name:
                typeof shortcut.titre === "object"
                  ? shortcut.titre.fr
                  : shortcut.titre,
              product_image: shortcut.image?.[0] || null,
              product_price: shortcut.prix_ttc ?? shortcut.prix,
            };
          }
          return null;
        })
        .filter((shortcut) => shortcut !== null);

      if (validShortcuts.length === 0) {
        swal({
          title: "Aucun raccourci!",
          text: "Veuillez ajouter au moins un produit à la grille",
          icon: "warning",
          button: "OK",
        });
        return;
      }

      // Prepare update data
      const updateData = {
        new_raccourci_name: raccourciName,
        shortcuts: validShortcuts,
        organisation_id: usedOrganisations,
      };

      const response = await updateRaccourci(id, updateData);

      if (response.success) {
        swal({
          title: "Succès!",
          text: "Le raccourci a été mis à jour",
          icon: "success",
          button: "OK",
        }).then(() => {
          navigate("/raccourcis");
        });
      } else {
        throw new Error(
          response.data.message || "Erreur lors de la mise à jour"
        );
      }
    } catch (error) {
      console.error("Error updating raccourci:", error);
      swal({
        title: "Erreur!",
        text: "Impossible de mettre à jour le raccourci: " + error.message,
        icon: "error",
        button: "OK",
      });
    }
  };

  const cancelEdit = () => {
    swal({
      title: "Annuler les modifications?",
      text: "Tous les changements non sauvegardés seront perdus.",
      icon: "warning",
      buttons: ["Continuer l'édition", "Annuler"],
      dangerMode: true,
    }).then((cancel) => {
      if (cancel) {
        navigate("/raccourcis");
      }
    });
  };

  if (loading) {
    return <GifLoader loading={true} />;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <section className="content">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-md-8">
            <h2>
              <i className="fa fa-edit text-primary mr-2"></i>
              Modifier le raccourci
            </h2>
          </div>
          <div className="col-md-4">
            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-success"
                onClick={saveChanges}
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <i className="fa fa-save"></i>
                Sauvegarder
              </button>
              <button
                className="btn btn-warning"
                onClick={clearShortcuts}
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <i className="fa fa-trash"></i>
                Effacer tout
              </button>
              <button
                className="btn btn-secondary"
                onClick={cancelEdit}
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <i className="fa fa-times"></i>
                Annuler
              </button>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Left: Available Products */}
          <div className="col-md-6">
            <div className="mb-3">
              <div className="card">
                <div className="card-body p-4">
                  <h4>
                    <i className="fa fa-arrows-alt text-primary mr-2"></i>
                    Produits disponibles ({menus.length})
                  </h4>
                </div>
              </div>
            </div>

            <div className="row">
              {menus.map((product) => (
                <div key={product.id} className="col-3 mb-3">
                  <DraggableProduct
                    product={product}
                    currency={currency}
                    isRaccourciMode={true}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Right: Raccourci Grid */}
          <div className="col-md-6">
            <div className="mb-3">
              <div className="card">
                <div className="card-body p-3">
                  <div className="input-group input-group-lg">
                    <input
                      type="text"
                      className="form-control form-control-lg m-0"
                      value={raccourciName}
                      onChange={(e) => setRaccourciName(e.target.value)}
                      placeholder="Nom du raccourci..."
                      style={{
                        fontWeight: "600",
                        borderColor: "#28a745",
                        borderRight: "none",
                      }}
                    />
                    <div className="input-group-append">
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setRaccourciName("Raccourcis")}
                        title="Réinitialiser"
                        style={{ borderColor: "#28a745" }}
                      >
                        <i className="fa fa-undo"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              {Array.from({ length: 12 }).map((_, index) => (
                <div key={index} className="col-3 mb-3">
                  <PlaceholderSlot
                    index={index}
                    product={shortcuts[index]}
                    onDrop={handleDrop}
                    onRemove={removeShortcut}
                    currency={currency}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </DndProvider>
  );
}

export default EditRaccourci;
